import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Briefcase, Users, FileText, MessageSquare, BarChart3, LogOut, TrendingUp, Clock, CheckCircle, UserCheck } from 'lucide-react';
import { Button } from '../ui/button';
import ResumeScreening from './ResumeScreening';
import CandidatePipeline from './CandidatePipeline';
import JobManagement from './JobManagement';
import CommunicationHub from './CommunicationHub';
import Analytics from './Analytics';

const Dashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState({
    total_jobs: 0,
    total_candidates: 0,
    active_candidates: 0,
    hired_count: 0
  });

  useEffect(() => {
    const token = localStorage.getItem('shansoats_token');
    const userData = localStorage.getItem('shansoats_user');

    if (!token || !userData) {
      navigate('/shansoats/login');
      return;
    }

    setUser(JSON.parse(userData));
    fetchStats();
  }, [navigate]);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('shansoats_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/shansoats/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('shansoats_token');
    localStorage.removeItem('shansoats_user');
    navigate('/shansoats/login');
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-900 to-black">
      <header className="bg-gray-800/50 backdrop-blur-sm border-b border-gray-700 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center">
                <Briefcase className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">ShansoATS</h1>
                <p className="text-xs text-gray-400">Recruitment Platform</p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-semibold text-white">{user.name || user.email}</p>
                <p className="text-xs text-gray-400">Recruiter</p>
              </div>
              <Button
                onClick={handleLogout}
                className="bg-gray-700 hover:bg-gray-600"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
          {[
            { id: 'overview', label: 'Overview', icon: TrendingUp },
            { id: 'jobs', label: 'Jobs', icon: Briefcase },
            { id: 'screening', label: 'Resume Screening', icon: FileText },
            { id: 'pipeline', label: 'Pipeline', icon: Users },
            { id: 'messages', label: 'Messages', icon: MessageSquare },
            { id: 'analytics', label: 'Analytics', icon: BarChart3 }
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold whitespace-nowrap transition-all ${
                  activeTab === tab.id
                    ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white'
                    : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        <div>
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <h2 className="text-3xl font-bold text-white mb-6">Recruitment Overview</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/20 border border-blue-500/30 rounded-xl p-6">
                  <div className="flex items-center justify-between mb-3">
                    <Briefcase className="w-8 h-8 text-blue-400" />
                    <span className="text-xs text-blue-400 font-semibold">ACTIVE JOBS</span>
                  </div>
                  <p className="text-3xl font-bold text-white mb-1">{stats.total_jobs}</p>
                  <p className="text-sm text-gray-400">Open Positions</p>
                </div>

                <div className="bg-gradient-to-br from-cyan-500/20 to-cyan-600/20 border border-cyan-500/30 rounded-xl p-6">
                  <div className="flex items-center justify-between mb-3">
                    <Users className="w-8 h-8 text-cyan-400" />
                    <span className="text-xs text-cyan-400 font-semibold">CANDIDATES</span>
                  </div>
                  <p className="text-3xl font-bold text-white mb-1">{stats.total_candidates}</p>
                  <p className="text-sm text-gray-400">Total Applicants</p>
                </div>

                <div className="bg-gradient-to-br from-yellow-500/20 to-yellow-600/20 border border-yellow-500/30 rounded-xl p-6">
                  <div className="flex items-center justify-between mb-3">
                    <Clock className="w-8 h-8 text-yellow-400" />
                    <span className="text-xs text-yellow-400 font-semibold">IN PROGRESS</span>
                  </div>
                  <p className="text-3xl font-bold text-white mb-1">{stats.active_candidates}</p>
                  <p className="text-sm text-gray-400">Active Pipeline</p>
                </div>

                <div className="bg-gradient-to-br from-green-500/20 to-green-600/20 border border-green-500/30 rounded-xl p-6">
                  <div className="flex items-center justify-between mb-3">
                    <UserCheck className="w-8 h-8 text-green-400" />
                    <span className="text-xs text-green-400 font-semibold">HIRED</span>
                  </div>
                  <p className="text-3xl font-bold text-white mb-1">{stats.hired_count}</p>
                  <p className="text-sm text-gray-400">Successful Hires</p>
                </div>
              </div>

              <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-6">
                <h3 className="text-xl font-bold text-white mb-4">Quick Actions</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {[
                    { label: 'Create Job', tab: 'jobs', icon: Briefcase, color: 'blue' },
                    { label: 'Screen Resumes', tab: 'screening', icon: FileText, color: 'cyan' },
                    { label: 'View Pipeline', tab: 'pipeline', icon: Users, color: 'purple' },
                    { label: 'Send Messages', tab: 'messages', icon: MessageSquare, color: 'green' },
                    { label: 'View Analytics', tab: 'analytics', icon: BarChart3, color: 'yellow' }
                  ].map((action) => {
                    const Icon = action.icon;
                    return (
                      <button
                        key={action.tab}
                        onClick={() => setActiveTab(action.tab)}
                        className={`flex flex-col items-center gap-2 p-4 bg-${action.color}-500/10 border border-${action.color}-500/30 rounded-lg hover:bg-${action.color}-500/20 transition-all`}
                      >
                        <Icon className={`w-6 h-6 text-${action.color}-400`} />
                        <span className="text-sm font-semibold text-white">{action.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'jobs' && <JobManagement onUpdate={fetchStats} />}
          {activeTab === 'screening' && <ResumeScreening />}
          {activeTab === 'pipeline' && <CandidatePipeline onUpdate={fetchStats} />}
          {activeTab === 'messages' && <CommunicationHub />}
          {activeTab === 'analytics' && <Analytics />}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
